import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

// App-wide watcher: notifies "your video is ready" even after you leave the editor.
export const RenderNotifier = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const tracked = useRef(new Set());
  const timer = useRef(null);

  useEffect(() => {
    if (!user) return;
    // ask for browser notification permission once (non-blocking)
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }
    const tick = async () => {
      try {
        const { data } = await api.get("/renders/active");
        const active = data.active || [];
        const activeIds = new Set(active.map((r) => r.project_id));
        // any tracked render that is no longer active => it finished
        for (const pid of Array.from(tracked.current)) {
          if (!activeIds.has(pid)) {
            tracked.current.delete(pid);
            try {
              const { data: proj } = await api.get(`/projects/${pid}`);
              const err = proj?.render_status?.error;
              if (err) {
                toast.error(err, { duration: 8000 });
              } else if (proj?.final_render) {
                notifyReady(proj.title, pid);
              }
              refreshUser();
            } catch { /* project gone */ }
          }
        }
        active.forEach((r) => tracked.current.add(r.project_id));
      } catch { /* offline / auth */ }
    };
    const notifyReady = (title, pid) => {
      const label = title ? `“${title}” is ready` : "Your video is ready";
      toast.success(`🎬 ${label}!`, {
        description: "Tap to preview & download it.",
        duration: 12000,
        action: { label: "Open", onClick: () => navigate(`/studio/${pid}`) },
      });
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          const n = new Notification("Viral3D — video ready", { body: label, tag: `render-${pid}` });
          n.onclick = () => { window.focus(); navigate(`/studio/${pid}`); };
        } catch { /* ignore */ }
      }
    };
    tick();
    timer.current = setInterval(tick, 5000);
    return () => { if (timer.current) clearInterval(timer.current); };
    // eslint-disable-next-line
  }, [user?.id]);

  return null;
};
